// useGroupChatState.js
// Handles group-invite lifecycle and the shared-post URL deep-link:
//   - handleInviteSuccess — adds new group to list, selects it, switches tab
//   - handleInviteFailure — clears pending token, shows error toast
//   - openSharedPost     — on mount, reads ?post= param and navigates to post view
//   - openDmChat         — opens a DM conversation from P2P or other contexts
//
// Returns: { handleInviteSuccess, handleInviteFailure, openDmChat }

import {
  useCallback,
  useEffect,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  toast,
} from "react-toastify";

import {
  TABS,
} from "../constants/tabs";

import {
  fetchPostWithComments,
} from "../components/postComment";

/* =========================================================
   IDB
========================================================= */
import {
  loadPosts,
  loadComments,
} from "../db/fsDB";

export function useGroupChatState({

  currentUser,
  socket,

  users,
  groups,

  myChatId,

  setGroups,
  setSelectedItem,
  setActiveTab,

  setCommentsByPost,

  setGlobalLivePosts,
  setFeedPosts,
  setInteractionPosts,

}) {

  const navigate =
    useNavigate();

  /* =========================================================
     OPEN SHARED POST
     OFFLINE-FIRST
  ========================================================= */
  useEffect(() => {

    if (!currentUser) return;

    let cancelled = false;

    (async () => {

      const params =
        new URLSearchParams(
          window.location.search
        );

      const postId =
        params.get("post");

      if (!postId) return;

      try {


        /* =========================================
           1. TRY IDB FIRST
        ========================================= */

        const allPosts =
          await loadPosts();

        let post =
          allPosts.find(
            (p) =>
              String(p.postId) ===
              String(postId)
          );

        let comments =
          await loadComments(postId);


        /* =========================================
           2. FALLBACK TO SERVER
        ========================================= */

        if (!post || !comments.length) {

      
          const result =
            await fetchPostWithComments(
              postId
            );

          if (cancelled) return;

          post =
            result?.post || post;

          comments =
            result?.comments || comments;
        }

        if (
          cancelled ||
          !post
        ) {
          return;
        }

        /* =========================================
           3. HYDRATE COMMENTS
        ========================================= */

        setCommentsByPost(
          (prev) => ({
            ...prev,
            [postId]:
              comments || [],
          })
        );

        /* =========================================
           4. HYDRATE FEEDS
        ========================================= */

        const mergePost =
          (posts = []) => {

            const exists =
              posts.some(
                (p) =>
                  String(p.postId) ===
                  String(post.postId)
              );

            if (exists) {

              return posts.map((p) =>
                String(p.postId) ===
                String(post.postId)
                  ? {
                      ...p,
                      ...post,
                    }
                  : p
              );
            }

            return [
              post,
              ...posts,
            ];
          };

        setGlobalLivePosts(
          mergePost
        );

        setFeedPosts(
          mergePost
        );

        setInteractionPosts(
          mergePost
        );

        /* =========================================
           5. OPEN POST PAGE
        ========================================= */

        navigate(
          "/",
          {
            replace: true,

            state: {
              view: "post",
              post,
            },
          }
        );

        window.history.replaceState(
          {},
          "",
          "/"
        );

      } catch (err) {


        toast.error(
          "Failed to open shared post"
        );
      }

    })();

    return () => {
      cancelled = true;
    };

  }, [
    currentUser,
    navigate,

    setCommentsByPost,

    setGlobalLivePosts,
    setFeedPosts,
    setInteractionPosts,
  ]);

  /* =========================================================
     INVITE LINK → FORCE CHATS TAB
  ========================================================= */
  useEffect(() => {

    if (!currentUser) return;

    const token =
      sessionStorage.getItem(
        "pendingInviteToken"
      );

    if (!token) return;


    setActiveTab(
      TABS.CHATS
    );

  }, [
    currentUser,
    setActiveTab,
  ]);

  /* =========================================================
     GROUP INVITE SUCCESS
  ========================================================= */
  const handleInviteSuccess =
    useCallback((group) => {

      sessionStorage.removeItem(
        "pendingInviteToken"
      );

      setGroups((prev) => {

        if (
          prev.some(
            (g) =>
              g.groupId ===
              group.groupId
          )
        ) {
          return prev;
        }

        return [
          ...prev,
          group,
        ];
      });

      setSelectedItem({
        type: "group",

        groupId:
          group.groupId,

        name:
          group.name,

        groupImageUrl:
          group.groupImageUrl ||
          null,
      });

      setActiveTab(
        TABS.CHATS
      );

    }, []);

  /* =========================================================
     GROUP INVITE FAILURE
  ========================================================= */
  const handleInviteFailure =
    useCallback((err) => {

      sessionStorage.removeItem(
        "pendingInviteToken"
      );

      if (
        err?.reason !==
        "cancelled"
      ) {

        toast.error(
          "Invite link is invalid or expired"
        );
      }

    }, []);

  /* =========================================================
     OPEN DM CHAT
  ========================================================= */
  const openDmChat = (
    peerChatId,
    peerUsername
  ) => {

    if (
      !peerChatId ||
      !socket
    ) {
      return;
    }

    const resolvedUsername =

      peerUsername ||

      users.find(
        (u) =>
          u.chatId ===
          peerChatId
      )?.username ||

      "@User";

    setActiveTab(
      TABS.CHATS
    );

    setSelectedItem(null);

    setTimeout(() => {

      setSelectedItem({
        type: "user",

        chatId:
          peerChatId,

        username:
          resolvedUsername,
      });

      /*
       * OFFLINE SAFE:
       * emit only if connected
       */
      if (socket.connected) {

        socket.emit(
          "select-chat",
          {
            recipientChatId:
              peerChatId,
          }
        );
      }

    }, 0);
  };

  return {

    handleInviteSuccess,
    handleInviteFailure,

    openDmChat,
  };
}